import { hasRole, useUser } from "@procurement-agents/auth";
import { Button, Table } from "@procurement-agents/ui";
import type { TemplateSummary } from "@procurement-agents/scope-api";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useScopeApi } from "../api";
import { relativeTime } from "../labels";

export function TemplatesList() {
  const { api, toast } = useScopeApi();
  const user = useUser();
  const nav = useNavigate();
  const [rows, setRows] = useState<TemplateSummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [newId, setNewId] = useState("");
  const [newName, setNewName] = useState("");
  const proc = hasRole(user, "procurement_user");
  // CHANGE_plan_control: a category several published templates serve, and which one a request starts from
  const shared = Object.entries((rows ?? []).filter((r) => r.published).reduce<Record<string, TemplateSummary[]>>((acc, r) => {
    for (const c of r.categories) if (c !== "*") (acc[c] ??= []).push(r);
    return acc;
  }, {})).filter(([, ts]) => ts.length > 1)
    .map(([c, ts]) => [c, [...ts].sort((a, b) => (b.published!.published_at ?? "").localeCompare(a.published!.published_at ?? ""))] as const);
  const load = () => api.listTemplates().then(setRows).catch(() => setRows([]));
  useEffect(() => { void load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportYaml = async () => { const text = await api.exportTemplatesYaml(); const url = URL.createObjectURL(new Blob([text], { type: "application/yaml" })); const a = document.createElement("a"); a.href = url; a.download = "templates.yaml"; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60_000); };
  const importYaml = async (file: File) => { try { const r = await api.importTemplatesYaml(await file.text()); toast(`Imported: ${r.published.map((p) => `${p.template_id} v${p.version}`).join(", ")}`); await load(); } catch (e) { toast(String((e as Error).message), "warn"); } };
  const create = async () => { try { await api.createTemplate({ template_id: newId.trim(), name: newName.trim(), categories: [] }); nav(`${newId.trim()}/draft`); } catch (e) { toast(String((e as Error).message), "warn"); } };
  return (
    <div className="page templates">
      <header className="row between">
        <h2>Templates</h2>
        <span className="row">
          <Link to="letterheads" className="btn quiet">Word letterheads</Link>
          {proc && <Button variant="quiet" onClick={exportYaml}>Export YAML</Button>}
          {proc && <label className="btn quiet">Import YAML<input type="file" accept=".yaml,.yml" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importYaml(f); e.target.value = ""; }} /></label>}
          {proc && <Button variant="primary" onClick={() => setCreating((c) => !c)}>New template</Button>}
        </span>
      </header>
      <p className="muted small">Templates decide which sections a scope of work needs, what each must cover, and which passages are fixed policy text. Edit a draft, preview it against a golden case, then publish it with a change note.</p>
      {creating && (
        <form className="new-template row wrap" onSubmit={(e) => { e.preventDefault(); void create(); }} onKeyDown={(e) => { if (e.key === "Escape") setCreating(false); }} aria-label="New template">
          <input value={newId} onChange={(e) => setNewId(e.target.value)} placeholder="template id (a-z, 0-9, _)" pattern="[a-z0-9_]+" required aria-label="Template id" autoFocus />
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name" required aria-label="Template name" />
          <Button variant="primary" type="submit">Create draft</Button>
          <Button variant="quiet" type="button" onClick={() => { setCreating(false); setNewId(""); setNewName(""); }}>Cancel</Button>
          <span className="muted small">A new template starts as an empty draft; add sections, give it categories, then publish.</span>
        </form>
      )}
      {rows === null ? <p className="muted">Loading…</p> : (
        <Table rows={rows} onRowClick={(r) => nav(r.template_id)} empty="No templates yet." columns={[
          { key: "name", header: "Template", render: (r) => <><strong>{r.name}</strong> <span className="muted small">{r.template_id}</span></> },
          { key: "categories", header: "Categories", render: (r) => r.categories.join(", ") || <span className="muted">—</span> },
          { key: "published", header: "Published", render: (r) => r.published ? <>v{r.published.version} <span className="muted small">· {r.published.sections} sections · {relativeTime(r.published.published_at)}</span></> : <span className="muted">not published</span> },
          { key: "draft", header: "Draft", render: (r) => r.draft ? <span className="chip">draft v{r.draft.version} in progress by {r.draft.updated_by ?? r.draft.created_by}</span> : <span className="muted">—</span> },
        ]} />
      )}
      {shared.length > 0 && (
        <div className="note small" role="note">
          {shared.map(([c, ts]) => (
            <p key={c}><strong>{c.replace(/_/g, " ")}</strong> is served by {ts.map((t) => t.name).join(" and ")}. A request starts from {ts[0]!.name}, the most recently published; procurement can choose another at the plan review.</p>
          ))}
        </div>
      )}
    </div>
  );
}
